var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import DataCacheService from '@/shared/services/DataCacheService';
import { dynamicSortMultiple } from '@/shared/utils/dynamicSort';
import config from '@/config';
import api from '@/shared/api';
import { mapSuggestedMailAddress } from '@/shared/services/ObjectDisplayService';
import { listItemMsg } from '@/shared/components/FormattedListValue';
export var Purpose;
(function (Purpose) {
    Purpose[Purpose["Other"] = 0] = "Other";
    Purpose[Purpose["BillingAndReminder"] = 1] = "BillingAndReminder";
    Purpose[Purpose["TaskManagement"] = 2] = "TaskManagement";
    Purpose[Purpose["BillingOnly"] = 3] = "BillingOnly";
    Purpose[Purpose["RemindersOnly"] = 4] = "RemindersOnly";
})(Purpose || (Purpose = {}));
var emptyHttpArrayPromise = function () { return Promise.resolve({ data: [] }); };
export var getEmailRecipients = function (relationId, invoiceStreamId, intl) {
    var emailAddressesPromise = relationId
        ? api.base.get("".concat(config.appUrls.api, "/api/address/MailSuggestions/").concat(relationId))
        : emptyHttpArrayPromise();
    var relationEmployeesPromise = relationId
        ? api.base.get("".concat(config.appUrls.api, "/api/Relation/Employees/").concat(relationId), {
            params: {
                invoiceStreamId: invoiceStreamId,
            },
        })
        : emptyHttpArrayPromise();
    return Promise.all([emailAddressesPromise, relationEmployeesPromise]).then(function (_a) {
        var emailAddresses = _a[0].data, relationEmployees = _a[1].data;
        var addresses = emailAddresses.reduce(function (result, x, idx) {
            if (!x)
                return result;
            if (result.some(function (y) { return y.value.toLowerCase() === x.emailAddress.toLowerCase(); }))
                return result;
            result.push(__assign(__assign({}, x), { key: "".concat(x.addressId, "-").concat(idx), value: x.emailAddress, label: mapSuggestedMailAddress(x, intl) }));
            return result;
        }, []);
        var employees = DataCacheService.getUsers()
            .map(function (x) {
            var label = "".concat(x.name, " (").concat(x.email, ")");
            var profiles = relationEmployees
                .filter(function (e) { return e.userId === x.id; })
                .map(function (e) { return intl.formatMessage(listItemMsg('employeeprofile', e.profile)); })
                .sort();
            if (profiles.length === 0)
                return {
                    key: x.id,
                    value: x.email,
                    label: label,
                    isRelationEmployee: false,
                    isActive: x.isActive,
                };
            return {
                key: x.id,
                value: x.email,
                label: "".concat(label, " (").concat(profiles.join(', '), ")"),
                isRelationEmployee: true,
                isActive: x.isActive,
            };
        })
            .sort(dynamicSortMultiple('-isRelationEmployee', 'label'));
        return { addresses: addresses, employees: employees };
    });
};
export var getCommunicationSettings = function (relationId, purpose) {
    var requestPromise = relationId
        ? api.base.get("".concat(config.appUrls.api, "/api/CommunicationSetting/get?id=").concat(relationId))
        : emptyHttpArrayPromise();
    return requestPromise.then(function (_a) {
        var data = _a.data;
        if (!data)
            return [];
        if (data && typeof purpose !== 'undefined') {
            return data.filter(function (x) { return x.purpose === purpose && x.medium === 1; });
        }
        return data.filter(function (x) { return x.medium === 1; });
    });
};
var findAddressesForType = function (settings, addresses, type) {
    return settings
        ? settings.reduce(function (a, e) {
            // purpose 0 = other, medium 1 = email, type = to/cc/bcc
            if (e.purpose === 0 && e.medium === 1 && e.type === type) {
                var address = addresses.find(function (addr) { return !!addr.value && addr.addressId === e.addressId; });
                if (address)
                    a.push(address.value);
            }
            return a;
        }, [])
        : [];
};
export var createEmptyRecipients = function () { return ({
    to: [''],
    cc: [],
    bcc: [],
    empTo: [],
    empCc: [],
    empBcc: [],
}); };
export var createRecipientsFromCommunicationSettings = function (settings, addresses) {
    if (addresses.length === 0)
        return {};
    var result = {
        to: findAddressesForType(settings, addresses, 0),
        cc: findAddressesForType(settings, addresses, 1),
        bcc: findAddressesForType(settings, addresses, 2),
    };
    if (result.to.length === 0) {
        var main = addresses.find(function (x) { return !!x.value && x.linkType === null && x.isMain; });
        if (main) {
            result.to = [main.value];
        }
    }
    return result;
};
export var validateEmail = function (value) {
    if (!value)
        return false;
    return /^[^@]+@.+\..+$/.test(value);
};
export var combineForRequest = function (recipients) { return ({
    to: __spreadArray(__spreadArray([], recipients.to, true), recipients.empTo, true).filter(function (x) { return validateEmail(x); }),
    cc: __spreadArray(__spreadArray([], recipients.cc, true), recipients.empCc, true).filter(function (x) { return validateEmail(x); }),
    bcc: __spreadArray(__spreadArray([], recipients.bcc, true), recipients.empBcc, true).filter(function (x) { return validateEmail(x); }),
}); };
export var validateRecipients = function (recipients) {
    if (recipients.to.length === 0 && recipients.empTo.length === 0)
        return false;
    if (recipients.to.some(function (x) { return !validateEmail(x); }))
        return false;
    if (recipients.cc.some(function (x) { return !validateEmail(x); }))
        return false;
    if (recipients.bcc.some(function (x) { return !validateEmail(x); }))
        return false;
    if (recipients.empTo.some(function (x) { return !validateEmail(x); }))
        return false;
    if (recipients.empCc.some(function (x) { return !validateEmail(x); }))
        return false;
    if (recipients.empBcc.some(function (x) { return !validateEmail(x); }))
        return false;
    return true;
};
export var sanitizeRecipients = function (recipients) {
    var result = __assign(__assign({}, createEmptyRecipients()), recipients);
    if (!result.to)
        result.to = [''];
    if (!result.cc)
        result.cc = [];
    if (!result.bcc)
        result.bcc = [];
    if (!result.empTo)
        result.empTo = [];
    if (!result.empCc)
        result.empCc = [];
    if (!result.empBcc)
        result.empBcc = [];
    if (result.to.length === 0 && result.empTo.length === 0) {
        result.to = [''];
    }
    return result;
};
